import { useMemo, useState } from 'react'
import { X, Bell } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import type { Booking } from '@/lib/types/booking'
import { useBookingActions } from '@/lib/hooks/useBookingActions'

interface Props {
  requests: Booking[]
}

export function RequestsWidget({ requests }: Props) {
  const { t } = useTranslation()
  const bookingActions = useBookingActions()
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())

  const items = useMemo(() =>
    requests
      .filter((b) => b.status === 'request' && !dismissed.has(b.documentId))
      .sort((a, b) => parseISO(`${a.date}T${a.time}`).getTime() - parseISO(`${b.date}T${b.time}`).getTime()),
  [requests, dismissed])

  const dismiss = (id: string) => setDismissed((prev) => new Set(prev).add(id))
  const mutate  = (id: string, status: 'approved' | 'canceled') =>
    bookingActions.updateStatus(id, status)

  return (
    <div className="rounded-[var(--radius-lg)] bg-[var(--color-bg-surface)] border border-[var(--color-border-subtle)] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border-subtle)]">
        <Bell className="h-3.5 w-3.5 text-[var(--color-accent-amber)]" />
        <p className="text-sm font-semibold text-[var(--color-text-primary)]">{t('requests.title')}</p>
        {items.length > 0 && (
          <span className="ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]">
            {items.length}
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] text-center py-6">
          {t('requests.noPendingRequests')}
        </p>
      ) : (
        <div className="divide-y divide-[var(--color-border-subtle)] max-h-[40vh] overflow-y-auto">
          {items.map((b) => (
            <div key={b.documentId} className="p-3 space-y-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{b.guest?.name ?? 'Walk-in'}</p>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    {format(parseISO(b.date), 'MMM d')} · {b.time} · {t('requests.guest', { count: b.people })}
                  </p>
                </div>
                <button
                  onClick={() => dismiss(b.documentId)}
                  className="shrink-0 p-1 rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => mutate(b.documentId, 'approved')}
                  disabled={false}
                  className="h-8 rounded-[var(--radius-md)] text-xs font-medium bg-[var(--color-accent-green)] text-white hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer"
                >
                  {t('requests.approve')}
                </button>
                <button
                  onClick={() => mutate(b.documentId, 'canceled')}
                  disabled={false}
                  className="h-8 rounded-[var(--radius-md)] text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {t('requests.decline')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
